import { supabase } from "./supabase";

export type LatestNewsLanguage = "en" | "ms" | "sabah" | "zh";

export type LatestNewsTranslation = {
  title: string;
  body: string;
};

export type LatestNewsTranslations = Partial<Record<LatestNewsLanguage, LatestNewsTranslation>>;

export type LatestNews = {
  title: string;
  body: string;
  translations: LatestNewsTranslations;
  published: boolean;
  updatedAt: string | null;
};

type LatestNewsRow = {
  title: string;
  body: string;
  translations: unknown;
  published: boolean;
  updated_at: string | null;
};

const latestNewsLanguages: LatestNewsLanguage[] = ["en", "ms", "sabah", "zh"];

function normalizeTranslations(value: unknown, english: LatestNewsTranslation): LatestNewsTranslations {
  const translations: LatestNewsTranslations = { en: english };
  if (!value || typeof value !== "object" || Array.isArray(value)) return translations;

  for (const language of latestNewsLanguages) {
    const candidate = (value as Record<string, unknown>)[language];
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
    const title = (candidate as Record<string, unknown>).title;
    const body = (candidate as Record<string, unknown>).body;
    if (typeof title === "string" && typeof body === "string" && title.trim() && body.trim()) {
      translations[language] = { title: title.trim(), body: body.trim() };
    }
  }

  return translations;
}

function normalizeLatestNews(row: LatestNewsRow): LatestNews {
  const english = { title: row.title, body: row.body };
  return {
    title: row.title,
    body: row.body,
    translations: normalizeTranslations(row.translations, english),
    published: row.published,
    updatedAt: row.updated_at,
  };
}

export function getLatestNewsTranslation(news: LatestNews, language: LatestNewsLanguage): LatestNewsTranslation {
  return news.translations[language] ?? news.translations.en ?? { title: news.title, body: news.body };
}

export async function readPublishedLatestNews(): Promise<LatestNews | null> {
  const { data, error } = await supabase
    .from("site_latest_news")
    .select("title,body,translations,published,updated_at")
    .eq("id", "latest")
    .eq("published", true)
    .maybeSingle();

  if (error) throw error;
  return data ? normalizeLatestNews(data as LatestNewsRow) : null;
}

export async function readAdminLatestNews(): Promise<LatestNews | null> {
  const { data, error } = await supabase
    .from("site_latest_news")
    .select("title,body,translations,published,updated_at")
    .eq("id", "latest")
    .maybeSingle();

  if (error) throw error;
  return data ? normalizeLatestNews(data as LatestNewsRow) : null;
}

export async function writeLatestNews(input: Pick<LatestNews, "title" | "body" | "translations" | "published">): Promise<LatestNews> {
  const translations = Object.fromEntries(
    latestNewsLanguages.flatMap((language) => {
      const translation = input.translations[language];
      return translation?.title.trim() && translation.body.trim()
        ? [[language, { title: translation.title.trim(), body: translation.body.trim() }]]
        : [];
    }),
  );
  const { data, error } = await supabase
    .from("site_latest_news")
    .upsert({
      id: "latest",
      title: input.title.trim(),
      body: input.body.trim(),
      translations,
      published: input.published,
      updated_at: new Date().toISOString(),
    }, { onConflict: "id" })
    .select("title,body,translations,published,updated_at")
    .single();

  if (error) throw error;
  return normalizeLatestNews(data as LatestNewsRow);
}
